import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import { sowService, SoWData } from '../services/sowService';
import EditableSoW from './EditableSoW';

interface SoWEditorProps {
  projectId: string;
  sowId: string;
  onComplete: () => void;
}

const SoWEditor: React.FC<SoWEditorProps> = ({ projectId, sowId, onComplete }) => {
  const [sow, setSow] = useState<SoWData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // 1. Load the SoW Data
  useEffect(() => {
    const loadSoW = async () => {
      try {
        const data = await sowService.getSoW(projectId, sowId);
        setSow(data);
      } catch (err) {
        console.error(err);
        setError('Failed to load Scope of Work.');
      } finally {
        setLoading(false);
      }
    };

    loadSoW();
  }, [projectId, sowId]);

  // 2. Handle Save
  const handleSave = async (updatedSow: SoWData) => {
    try {
      setSaving(true);
      // Update the backend with the new structure
      // Note: We are sending the cost items. If your backend supports updating sections too,
      // you might need to adjust the API endpoint or service method.
      // For now, we assume sowService.updateSoW handles the cost items.
      
      await sowService.updateSoW(projectId, sowId, updatedSow.tender_document);
      
      // Update local state
      setSow(updatedSow);
      
      // Exit edit mode
      onComplete();
    } catch (err) {
      console.error(err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !sow) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error || 'No Scope of Work found.'}
      </Alert>
    );
  }

  // 3. Render the Editable UI
  return (
    <Box>
      {saving && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, left: 0, right: 0, bottom: 0, 
          bgcolor: 'rgba(255,255,255,0.7)', 
          zIndex: 10, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Saving changes...</Typography>
        </Box>
      )}
      
      <EditableSoW 
        sowData={sow} 
        onSave={handleSave} 
        onCancel={onComplete} 
      />
    </Box>
  );
};

export default SoWEditor;